import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Button, Input, FormWrap, toast } from 'day8-ui';

import { loginUser, checkUser, validateUser, saveLoggedInUser } from '../../actions/user';
import history from '../../history';
import { normalizeRedirectTo, shouldUseFullPageRedirect } from '../../helpers/redirectTo';

class Login extends Component {
  constructor(props) {
    super(props);

    this.state = {
      email: '',
      password: '',
      code: '',
      loading: true,
      step: 'email', // 'email' | 'otp' | 'password' | 'merge'
      errors: {},
      token: null,
      mergeSiblingIds: [],
      mergeSiblings: [],
      adoptEmailOnMerge: null,
      merging: false,
      // Held until merge accept/skip — saving to Redux earlier redirects away from /account/login
      pendingMergeUser: null
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.onSuccess = this.onSuccess.bind(this);
    this.requestOtp = this.requestOtp.bind(this);
    this.verifyOtp = this.verifyOtp.bind(this);
    this.acceptMerge = this.acceptMerge.bind(this);
    this.skipMerge = this.skipMerge.bind(this);
  }

  componentDidMount() {
    this.bootstrap();
  }

  onSuccess() {
    const { location, redirectTo } = this.props;
    const returnTo = location && location.state && location.state.returnTo;
    if (redirectTo) {
      const target = normalizeRedirectTo(redirectTo);
      if (shouldUseFullPageRedirect(target)) {
        window.location.href = target;
        return undefined;
      }
      return history.push(target);
    }
    // Used for react router
    return returnTo ? history.push(returnTo) : history.push('/account');
  }

  finishLogin(user) {
    const { validateUser, shouldValidateUser } = this.props;
    this.props.saveLoggedInUser(user);
    toast.success('Login successful');
    if (shouldValidateUser) {
      validateUser(this.onSuccess);
    } else {
      this.onSuccess();
    }
  }

  async bootstrap() {
    this.setState({ loading: true });
    const response = await this.props.checkUser();
    const token = response.token;

    try {
      const statusRes = await fetch('/api/authentication/otp/status', {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      const status = await statusRes.json();
      if (status?.data?.passwordFallback) {
        this.setState({ step: 'password', loading: false, token });
        return;
      }
      if (status?.status === 'success' && status?.data?.has_active_otp && status?.data?.email) {
        this.setState({
          email: status.data.email,
          step: 'otp',
          loading: false,
          token
        });
        return;
      }
    } catch {
      // Fall through to email step
    }

    this.setState({ token, loading: false });
  }

  async requestOtp() {
    const { email } = this.state;
    this.setState({ loading: true, errors: {} });
    try {
      const response = await fetch('/api/authentication/otp/request', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await response.json();
      if (data?.data?.passwordFallback) {
        toast.error(data.message || 'Code login unavailable — use your password');
        this.setState({ step: 'password', loading: false });
        return;
      }
      if (data.status === 'success') {
        toast.success('Verification code sent to your email');
        this.setState({ step: 'otp', loading: false });
        return;
      }
      const message = data.message || 'Failed to send verification code';
      toast.error(message);
      this.setState({
        loading: false,
        errors: { email: { notificationMessage: message } }
      });
    } catch {
      toast.error('Network error. Please try again.');
      this.setState({
        loading: false,
        errors: { email: { notificationMessage: 'Network error. Please try again.' } }
      });
    }
  }

  async verifyOtp() {
    const { code } = this.state;
    this.setState({ loading: true, errors: {} });
    try {
      const response = await fetch('/api/authentication/otp/verify', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code })
      });
      const data = await response.json();

      if (data?.data?.passwordFallback) {
        toast.error(data.message || 'Code login unavailable — use your password');
        this.setState({ step: 'password', loading: false });
        return;
      }

      if (response.status === 409) {
        toast.error('Unable to sign in with this account. Please contact support.');
        this.setState({ loading: false });
        return;
      }

      if (data.status === 'success' && data.data?.user) {
        const user = data.data.user;
        // Spec: use the keeper, offer a merge — never auto-merge.
        // Do not saveLoggedInUser yet: AccountRouting redirects /account/login → /account
        // as soon as Redux has a user, which unmounts this screen before the offer renders.
        if (data.data.mergeCandidate && Array.isArray(data.data.siblings)
          && data.data.siblings.length > 0) {
          this.setState({
            step: 'merge',
            loading: false,
            mergeSiblings: data.data.siblings,
            mergeSiblingIds: data.data.siblings.map(s => s.id),
            adoptEmailOnMerge: data.data.adoptEmailOnMerge || null,
            pendingMergeUser: user
          });
          return;
        }
        if (data.data.mergeCandidate && Array.isArray(data.data.siblingProfileIds)
          && data.data.siblingProfileIds.length > 0) {
          this.setState({
            step: 'merge',
            loading: false,
            mergeSiblingIds: data.data.siblingProfileIds,
            mergeSiblings: [],
            adoptEmailOnMerge: data.data.adoptEmailOnMerge || null,
            pendingMergeUser: user
          });
          return;
        }
        this.finishLogin(user);
        return;
      }

      const message = data.message || 'Invalid verification code';
      toast.error(message);
      this.setState({
        loading: false,
        errors: { code: { notificationMessage: message } }
      });
    } catch {
      toast.error('Network error. Please try again.');
      this.setState({
        loading: false,
        errors: { code: { notificationMessage: 'Network error. Please try again.' } }
      });
    }
  }

  async acceptMerge() {
    const { mergeSiblingIds, pendingMergeUser } = this.state;
    this.setState({ merging: true });
    try {
      for (const siblingProfileId of mergeSiblingIds) {
        const response = await fetch('/api/account/merge-sibling', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ siblingProfileId })
        });
        const data = await response.json();
        if (data.status !== 'success') {
          toast.error(data.message || 'Could not combine accounts. Contact support.');
          this.setState({ merging: false });
          return;
        }
      }
      toast.success('Accounts combined');
      this.setState({ merging: false, mergeSiblingIds: [], pendingMergeUser: null });
      if (pendingMergeUser) {
        this.finishLogin(pendingMergeUser);
      } else {
        this.onSuccess();
      }
    } catch {
      toast.error('Network error. Please try again or contact support.');
      this.setState({ merging: false });
    }
  }

  skipMerge() {
    const { pendingMergeUser } = this.state;
    this.setState({ mergeSiblingIds: [], pendingMergeUser: null });
    toast.success('Continuing with this account');
    if (pendingMergeUser) {
      this.finishLogin(pendingMergeUser);
    } else {
      this.onSuccess();
    }
  }

  async handlePasswordSubmit() {
    const { validateUser, shouldValidateUser, resetTo } = this.props;
    this.setState({ loading: true });
    const { email, password, token } = this.state;
    const result = await this.props.loginUser(email, password, token);

    // The account must set a new password before it can log in. Send them
    // straight to the recovery form rather than showing a field error, and carry
    // the address across so they do not have to retype it.
    if (result.code === 'password_reset_required') {
      toast.error(result.message);
      this.setState({ loading: false, errors: {} });
      // resetTo is set by LoginModal (an object with state/search); on the
      // standalone page it is undefined, so target the route directly.
      const resetState = { forcedResetEmail: email, description: result.message };
      let target = { pathname: '/account/reset-password', state: resetState };
      if (resetTo) {
        target = { ...resetTo, state: { ...(resetTo.state || {}), ...resetState } };
      }
      history.push(target);
      return;
    }

    if (result.status === 'success') {
      toast.success('Login successful');
      if (shouldValidateUser) {
        validateUser(this.onSuccess);
      } else {
        this.onSuccess();
      }
    }
    const errors = {};
    if (result?.errors) {
      toast.error('Please check your details and try again');
      Object.keys(result.errors).forEach(error => {
        errors[error] = {
          notificationType: 'error',
          notificationMessage: Array.isArray(result.errors[error]) ? result.errors[error][0] : Object.entries(result.errors[error])[0][1][0],
          showNotification: true
        };
      });
    }
    this.setState({ loading: false, errors });
  }

  async handleSubmit(e) {
    e.preventDefault();
    const { step } = this.state;
    if (step === 'email') {
      await this.requestOtp();
    } else if (step === 'otp') {
      await this.verifyOtp();
    } else {
      await this.handlePasswordSubmit();
    }
  }

  renderEmailStep() {
    const { loading, errors, email } = this.state;
    const { signupTo } = this.props;
    return (
      <div>
        <FormWrap onSubmit={this.handleSubmit} loading={loading}>
          <div className='dui-flex dui-flex-col dui-gap-4'>
            <Input
              id="email"
              className='dui-w-full'
              label="Email address"
              placeholder="Your email"
              value={email}
              onChange={e => this.setState({ email: e.target.value })}
              errorMessage={errors && errors.email?.notificationMessage}
            />
            <Button type="submit" className='dui-w-full'>
              {loading ? 'Sending code...' : 'Continue'}
            </Button>
          </div>
        </FormWrap>
        <p className="dui-mt-4 dui-text-sm dui-text-gray-400">
          Don‘t have an account?{' '}
          <Link to={signupTo || 'signup'} className="dui-underline dui-font-bold dui-text-primary-950">
            Sign up
          </Link>
        </p>
        {/* Password is the fallback for when the code cannot be delivered, not a second
            front door: kept reachable but deliberately quiet. Reset lives on that step. */}
        <p className="dui-mt-8 dui-text-xs dui-text-gray-400">
          Trouble signing in?{' '}
          <button
            type="button"
            className="dui-underline dui-text-xs dui-text-gray-400"
            onClick={() => this.setState({ step: 'password', errors: {} })}
          >
            Use your password
          </button>
        </p>
      </div>
    );
  }

  renderOtpStep() {
    const { loading, errors, email, code } = this.state;
    return (
      <div>
        <FormWrap onSubmit={this.handleSubmit} loading={loading}>
          <div className='dui-flex dui-flex-col dui-gap-4'>
            <p className="dui-text-sm dui-text-gray-600">
              Enter the 6-digit code sent to {email}
            </p>
            <Input
              id="code"
              className='dui-w-full'
              label="Verification code"
              placeholder="123456"
              value={code}
              onChange={e => this.setState({ code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
              errorMessage={errors && errors.code?.notificationMessage}
            />
            <Button type="submit" className='dui-w-full' disabled={loading || code.length !== 6}>
              {loading ? 'Verifying...' : 'Sign in'}
            </Button>
            <Button
              type="button"
              className='dui-w-full'
              onClick={() => this.setState({ step: 'email', code: '', errors: {} })}
            >
              ← Back to email
            </Button>
          </div>
        </FormWrap>
        <p className="dui-mt-8 dui-text-xs dui-text-gray-400">
          Didn‘t get the code?{' '}
          <button
            type="button"
            className="dui-underline dui-text-xs dui-text-gray-400"
            onClick={() => this.setState({ step: 'password', errors: {} })}
          >
            Use your password
          </button>
        </p>
      </div>
    );
  }

  renderMergeStep() {
    const { merging, mergeSiblingIds, mergeSiblings, email, adoptEmailOnMerge } = this.state;
    return (
      <div className='dui-flex dui-flex-col dui-gap-4'>
        <h2 className="dui-text-lg dui-font-semibold">Another account is linked to this login</h2>
        <p className="dui-text-sm dui-text-gray-600">
          You signed in successfully{email ? ` as ${email}` : ''}. We also found
          {mergeSiblingIds.length === 1 ? ' another account' : ` ${mergeSiblingIds.length} other accounts`}
          {' '}linked to the same gateway identity (matched by email or an associated address).
          Combining moves bookings and related data into this login. This cannot be undone automatically.
        </p>
        {adoptEmailOnMerge && (
          <p className="dui-text-sm dui-text-gray-600">
            Your account email will also change to <strong>{adoptEmailOnMerge}</strong>, the address
            you signed in with. Emails from us will go there.
          </p>
        )}
        {mergeSiblings.length > 0 && (
          <ul className="dui-text-sm dui-list-disc dui-pl-5 dui-space-y-2">
            {mergeSiblings.map(sibling => {
              const fullName = [sibling.firstName, sibling.lastName].filter(Boolean).join(' ');
              const nameSuffix = fullName ? ` — ${fullName}` : '';
              let bookingSuffix = '';
              if (typeof sibling.bookingCount === 'number') {
                const plural = sibling.bookingCount === 1 ? '' : 's';
                bookingSuffix = ` (${sibling.bookingCount} booking${plural})`;
              }
              return (
                <li key={sibling.id}>
                  <strong>{sibling.email || 'No email'}</strong>
                  {nameSuffix}
                  {bookingSuffix}
                </li>
              );
            })}
          </ul>
        )}
        <Button
          type="button"
          className='dui-w-full'
          disabled={merging}
          onClick={this.acceptMerge}
        >
          {merging ? 'Combining…' : 'Combine accounts'}
        </Button>
        <Button
          type="button"
          className='dui-w-full'
          disabled={merging}
          onClick={this.skipMerge}
        >
          Continue without combining
        </Button>
        <p className="dui-text-sm dui-text-gray-400">
          Need help?{' '}
          <a href="mailto:support@theyachtweek.com" className="dui-underline dui-font-semibold">
            Contact support
          </a>
        </p>
      </div>
    );
  }

  renderPasswordStep() {
    const { loading, errors, email, password } = this.state;
    const { signupTo, resetTo } = this.props;
    return (
      <div>
        <FormWrap onSubmit={this.handleSubmit} loading={loading}>
          <div className='dui-flex dui-flex-col dui-gap-4'>
            <Input
              id="email"
              className='dui-w-full'
              label="Email address"
              placeholder="Your email"
              value={email}
              onChange={e => this.setState({ email: e.target.value })}
              errorMessage={errors && errors.email?.notificationMessage}
            />
            <Input
              id="password"
              className='dui-w-full'
              type="password"
              label="Password"
              placeholder="Your password"
              value={password}
              onChange={e => this.setState({ password: e.target.value })}
              errorMessage={errors && errors.password?.notificationMessage}
            />
            <Button type="submit" className='dui-w-full'>
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
            <Button
              type="button"
              className='dui-w-full'
              onClick={() => this.setState({ step: 'email', password: '', errors: {} })}
            >
              ← Sign in with code
            </Button>
          </div>
        </FormWrap>
        <p className="dui-mt-4 dui-text-sm">
          <Link to={resetTo || 'reset-password'} className="dui-font-semibold dui-text-gray-400">
            Forgot password?
          </Link>
        </p>
        <p className="dui-mt-4 dui-text-sm dui-text-gray-400">
          Don‘t have an account?{' '}
          <Link to={signupTo || 'signup'} className="dui-underline dui-font-bold dui-text-primary-950">
            Sign up
          </Link>
        </p>
      </div>
    );
  }

  render() {
    const { step } = this.state;
    if (step === 'otp') {
      return this.renderOtpStep();
    }
    if (step === 'password') {
      return this.renderPasswordStep();
    }
    if (step === 'merge') {
      return this.renderMergeStep();
    }
    return this.renderEmailStep();
  }
}

Login.propTypes = {
  redirectTo: PropTypes.string,
  location: PropTypes.object.isRequired,
  loginUser: PropTypes.func.isRequired,
  validateUser: PropTypes.func.isRequired,
  checkUser: PropTypes.func.isRequired,
  saveLoggedInUser: PropTypes.func.isRequired,
  signupTo: PropTypes.object,
  resetTo: PropTypes.object,
  shouldValidateUser: PropTypes.bool
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      loginUser,
      checkUser,
      validateUser,
      saveLoggedInUser
    },
    dispatch
  );

export default connect(
  null,
  mapDispatchToProps
)(Login);
